<template>
  <div class="next-of-kin-step">
    <p class="step-intro pm-txt-color">
      Please nominate a next of kin and provide their details below. Please note that your next of kin will not have
      authority to operate your account or make changes to your loan.
    </p>

    <div class="inputs inputs-personal">
      <TextField
        width="100%"
        placeholder="First name"
        label="First Name"
        :maxLength="NAME_MAX_LENGTH"
        :value="value.firstName"
        :errorMessages="errors.firstName"
        @onInput="updateField('firstName', $event)"
      />
      <TextField
        width="100%"
        placeholder="Last name"
        label="Last Name"
        :maxLength="NAME_MAX_LENGTH"
        :value="value.lastName"
        :errorMessages="errors.lastName"
        @onInput="updateField('lastName', $event)"
      />
      <TextField
        width="100%"
        placeholder="Email address"
        label="Email"
        :value="value.email"
        :errorMessages="errors.email"
        @onInput="updateField('email', $event)"
      />
      <TextField
        width="100%"
        placeholder="Phone number"
        label="Phone"
        type="tel"
        numeric
        maxLength="10"
        :value="value.mobilePhoneNumber"
        :errorMessages="errors.mobilePhoneNumber"
        @onInput="updateField('mobilePhoneNumber', $event)"
      />
    </div>

    <div class="mt-3 inputs inputs-details">
      <Dropdown
        width="100%"
        placeholder="Relationship"
        label="Relationship"
        panelClass="uncon-relationship-panel"
        optionLabel="label"
        optionValue="value"
        :value="value.relationship"
        :items="relationshipItems"
        :errorMessages="errors.relationship"
        @change="updateField('relationship', $event)"
      />
      <div>
        <label class="p-float-label hk-grotest-medium">Date of Birth</label>
        <DatePicker
          width="100%"
          :value="value.dateOfBirth"
          :maxDate="today"
          :yearRange="dateOfBirthYearRange"
          @change="updateField('dateOfBirth', $event)"
        />
        <label v-if="errors.dateOfBirth" class="error-text">{{ errors.dateOfBirth }}</label>
      </div>
      <InputAddress
        class="address-field"
        :value="value.address"
        @onAddressChange="onAddressChange"
        :errorMessages="errors.address"
      />
    </div>

    <p class="pm-txt-color consent-text">
      You must first obtain consent from your nominated next of kin before providing their personal details. To see how
      Bridgit collects, discloses, uses, stores, or otherwise handles personal information you and/or your next of kin
      may view Bridgit's Privacy Policy at
      <a class="privacy-policy-link" :href="privacyPolicyUrl" target="_blank" rel="noopener noreferrer"
        >www.bridgit.com.au/privacy-policy</a
      >.
    </p>

    <div class="consent consent-first">
      <Checkbox
        id="next-of-kin-notified"
        :label="nextOfKinNotifiedLabel"
        :value="value.nextOfKinNotified"
        @getInputValue="updateField('nextOfKinNotified', $event)"
      />
      <label v-if="errors.nextOfKinNotified" class="error-text">{{ errors.nextOfKinNotified }}</label>
    </div>

    <div class="mt-3 consent">
      <Checkbox
        id="next-of-kin-privacy-policy-provided"
        :label="privacyPolicyProvidedLabel"
        :value="value.privacyPolicyProvided"
        @getInputValue="updateField('privacyPolicyProvided', $event)"
      />
      <label v-if="errors.privacyPolicyProvided" class="error-text">{{ errors.privacyPolicyProvided }}</label>
    </div>
  </div>
</template>

<script>
import InputAddress from '@/components/common/InputAddress.vue';

const PRIVACY_POLICY_URL = 'https://www.bridgit.com.au/privacy-policy';

// Matches the contacts table column, so an over-long name is capped as it is typed rather than
// rejected by the API on submit.
const NAME_MAX_LENGTH = 50;

// Comfortably older than the oldest permitted next of kin, so the year list never truncates a
// selectable birth year.
const DATE_OF_BIRTH_EARLIEST_YEAR = 1920;

// Labels are spelled out rather than derived from the value, so "spouse_partner" can read
// "Spouse/Partner". The values are the ContactRelationship enum the API expects.
const RELATIONSHIP_OPTIONS = [
  { value: 'spouse_partner', label: 'Spouse/Partner' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'parent', label: 'Parent' },
  { value: 'legal_representative', label: 'Legal Representative' },
  { value: 'other', label: 'Other' },
];

const NEXT_OF_KIN_NOTIFIED_TEXT =
  'I have notified my nominated next of kin that I have provided their details as set out above to Bridgit and ' +
  'have received consent from my next of kin to do so.';

const PRIVACY_POLICY_PROVIDED_TEXT =
  "I have provided my nominated next of kin with Bridgit's Privacy Policy and they have agreed to be my " +
  'nominated next of kin.';

export default {
  components: { InputAddress },
  props: {
    value: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      NAME_MAX_LENGTH,
      relationshipItems: RELATIONSHIP_OPTIONS,
      privacyPolicyUrl: PRIVACY_POLICY_URL,
      nextOfKinNotifiedLabel: NEXT_OF_KIN_NOTIFIED_TEXT,
      privacyPolicyProvidedLabel: PRIVACY_POLICY_PROVIDED_TEXT,
    };
  },
  computed: {
    // A date of birth cannot be in the future. The picker's own default year range stops at a
    // hard-coded past year, which does not constrain navigation on this PrimeVue version, so the
    // ceiling is set explicitly here. Whether the age falls inside the permitted 18 to 80 band is
    // still decided on submit, where the message explaining the band lives.
    today() {
      return new Date();
    },
    dateOfBirthYearRange() {
      return `${DATE_OF_BIRTH_EARLIEST_YEAR}:${new Date().getFullYear()}`;
    },
  },
  methods: {
    updateField(fieldName, fieldValue) {
      this.$emit('update', { ...this.value, [fieldName]: fieldValue });
    },
    onAddressChange(address) {
      this.$emit('update', { ...this.value, address });
    },
  },
};
</script>

<style lang="scss" scoped>
// Type ramp taken from the design: 18 / 16 / 14 below the dialog's own 36px title.
.step-intro {
  font-size: 1.125rem;
  line-height: 1.5rem;
  margin-bottom: 1.25rem;
}

// The consent copy and both acknowledgements run to the full width of the dialog, level with the
// inputs above them. A max-width here left them measurably short of the fields.
.consent-text {
  font-size: 1rem;
  line-height: 1.5rem;
  margin-top: 1.25rem;
  margin-bottom: 0;
}

// The design leaves a clear break between the consent copy and the acknowledgements.
.consent-first {
  margin-top: 2.5rem;
}

.inputs {
  display: grid;
  gap: 1.25rem;
  align-items: start;
}

// Both rows share one four-column track so every field edge in the lower row lines up with the
// row above it. Relationship and date of birth take a column each; the address takes the
// remaining two, ending flush with the phone field.
.inputs-personal,
.inputs-details {
  grid-template-columns: repeat(4, 1fr);
}

.inputs-details .address-field {
  grid-column: span 2;
}

@media (max-width: 992px) {
  .inputs-personal,
  .inputs-details {
    grid-template-columns: 1fr;
  }

  // Left spanning two columns, the address would generate a second implicit column and break the
  // single-column stacking.
  .inputs-details .address-field {
    grid-column: auto;
  }
}

// The design keeps the privacy policy link in the body text colour, underlined, rather than the
// browser/Bootstrap link blue.
.privacy-policy-link {
  color: inherit;
  text-decoration: underline;
}

// The acknowledgements sit a step below body copy in the design.
.consent :deep(.label-text) {
  font-size: 0.875rem;
  line-height: 1.4;
}

// PrimeFlex gives every checkbox field a bottom margin, which spaces the two acknowledgements. On
// the last one it becomes trailing space that pushes the dialog footer below where the design has it.
.consent:last-child :deep(.p-field-checkbox) {
  margin-bottom: 0;
}

.error-text {
  color: red;
  font-size: 14px;
}
</style>

<style lang="scss">
.uncon-relationship-panel.p-dropdown-panel .p-dropdown-items {
  padding-left: 0;
  padding-right: 0;
}
.uncon-relationship-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
</style>
